<template>
  <div class="goal-status__button-group__container">
    <b-tooltip label="Hit" type="is-dark" position="is-top">
      <b-button
        size="is-small"
        :class="[
          'goal-status-button',
          { 'has-text-grey-light': (!goal || goal.goalStatus !== Types.GOAL_HIT) && !isHitHovered }
        ]"
        :type="(goal && goal.goalStatus === Types.GOAL_HIT) || isHitHovered ? 'is-success' : 'is-white'"
        rounded
        :disabled="!isGoalEditable || isGoalStatusUpdating[Types.GOAL_MISS] || isGoalStatusUpdating[Types.GOAL_CHANGED]"
        :loading="isGoalStatusUpdating[Types.GOAL_HIT]"
        @mouseover.native="isHitHovered = true"
        @focus.native="isHitHovered = true"
        @mouseleave.native="isHitHovered = false"
        @blur.native="isHitHovered = false"
        @click="handleUpdateGoalStatus({ goalStatus: Types.GOAL_HIT })"
      >
        <b-icon icon="check" custom-size="mdi-18px" />
      </b-button>
    </b-tooltip>
    <b-tooltip label="Miss" type="is-dark" position="is-top">
      <b-button
        size="is-small"
        :class="[
          'goal-status-button',
          { 'has-text-grey-light': (!goal || goal.goalStatus !== Types.GOAL_MISS) && !isMissHovered }
        ]"
        :type="(goal && goal.goalStatus === Types.GOAL_MISS) || isMissHovered ? 'is-danger' : 'is-white'"
        rounded
        :disabled="!isGoalEditable || isGoalStatusUpdating[Types.GOAL_HIT] || isGoalStatusUpdating[Types.GOAL_CHANGED]"
        :loading="isGoalStatusUpdating[Types.GOAL_MISS]"
        @mouseover.native="isMissHovered = true"
        @focus.native="isMissHovered = true"
        @mouseleave.native="isMissHovered = false"
        @blur.native="isMissHovered = false"
        @click="handleUpdateGoalStatusWithExplanation(Types.GOAL_MISS)"
      >
        <b-icon icon="cancel" custom-size="mdi-18px" />
      </b-button>
    </b-tooltip>
    <b-tooltip label="Goal Changed" type="is-dark" position="is-top">
      <b-button
        size="is-small"
        :class="[
          'goal-status-button',
          { 'has-text-grey-light': (!goal || goal.goalStatus !== Types.GOAL_CHANGED) && !isGoalChangedHovered }
        ]"
        :type="(goal && goal.goalStatus === Types.GOAL_CHANGED) || isGoalChangedHovered ? 'is-warning' : 'is-white'"
        rounded
        :disabled="!isGoalEditable || isGoalStatusUpdating[Types.GOAL_HIT] || isGoalStatusUpdating[Types.GOAL_MISS]"
        :loading="isGoalStatusUpdating[Types.GOAL_CHANGED]"
        @mouseover.native="isGoalChangedHovered = true"
        @focus.native="isGoalChangedHovered = true"
        @mouseleave.native="isGoalChangedHovered = false"
        @blur.native="isGoalChangedHovered = false"
        @click="handleUpdateGoalStatusWithExplanation(Types.GOAL_CHANGED)"
      >
        <b-icon icon="asterisk" custom-size="mdi-18px" />
      </b-button>
    </b-tooltip>

    <goal-status-detail-modal
      :is-modal-open="isGoalDetailModalOpen"
      :goal-status="goalDetailStatus"
      @modal-close="handleUpdateGoalStatus"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import * as GoalActions from '@/store/actions/Goal.actions';
import * as Types from '@/constants/Types';
import GoalStatusDetailModal from '@/components/GoalStatusDetailModal.vue';

export default {
  name: 'GoalStatusIconGroup',

  components: {
    GoalStatusDetailModal,
  },

  props: {
    goalId: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      Types,
      isHitHovered: false,
      isMissHovered: false,
      isGoalChangedHovered: false,
      isGoalStatusUpdating: {
        [Types.GOAL_HIT]: false,
        [Types.GOAL_MISS]: false,
        [Types.GOAL_CHANGED]: false,
      },
      createWorkspaceError: null,
      goalDetailStatus: null,
      isGoalDetailModalOpen: false,
    };
  },

  computed: {
    ...mapState({
      goals: state => state.goal.goals,
      loggedInUserId: state => state.auth.loggedInUserId,
    }),
    ...mapGetters([
      'activeWorkspace',
    ]),
    goal() {
      return this.goalId ? this.goals[this.goalId] : null;
    },
    isGoalEditable() {
      return this.goalId;
    },
  },

  methods: {
    ...mapActions({
      updateGoal: GoalActions.UPDATE_GOAL,
    }),
    handleUpdateGoalStatusWithExplanation(selectedStatus) {
      if (this.goal && this.goal.goalStatus === selectedStatus) {
        this.handleUpdateGoalStatus({ goalStatus: selectedStatus });
      } else if (this.activeWorkspace.promptForGoalExplanationPreference
        && (selectedStatus === Types.GOAL_MISS || selectedStatus === Types.GOAL_CHANGED)) {
        this.isGoalDetailModalOpen = true;
        this.goalDetailStatus = selectedStatus;
      } else {
        this.handleUpdateGoalStatus({ goalStatus: selectedStatus });
      }
    },
    handleUpdateGoalStatus(statusPayload) {
      const { goalStatus, goalStatusExplanation } = statusPayload;
      this.isGoalDetailModalOpen = false;

      let newGoalStatus = goalStatus;
      const newGoalStatusExplanation = goalStatusExplanation || null;

      this.isGoalStatusUpdating[newGoalStatus] = true;

      // Allow unmarking a goal status by clicking on the active status button.
      if (this.goal && this.goal.goalStatus === newGoalStatus) {
        newGoalStatus = Types.GOAL_UNMARKED;
      }

      this.updateGoal({
        goalId: this.goalId,
        updatedGoal: {
          goalStatus: newGoalStatus,
          goalStatusExplanation: newGoalStatusExplanation,
        },
      })
        .then(() => { this.isGoalStatusUpdating[goalStatus] = false; })
        .catch(() => { this.isGoalStatusUpdating[goalStatus] = false; });
    },
  },
};
</script>

<!-- Note CSS is NOT scoped here so the b-icon component can be aware of these classes. -->
<style lang="scss">
.goal-status-button {
  opacity: 1 !important;

  // 'bump' the icon down inside the button so it appears centered.
  // Default line-height makes it looks slightly off when using circle buttons.
  .icon {
    margin-top: 2px;
  }
}

.goal-status__button-group__container {
  .b-tooltip:not(last-child) {
    margin-right: $global-whitespace-xsmall;
  }
}
</style>
